import { addDays } from '@skylab-shared/utils';
import { CalendarUtils } from './calendar-utils';

interface IDayDefinition {
    name: string;
    type: DayType;

    calculateDate(year: number): Date | null;
}

enum DayType {
    PublicHoliday = 1 << 0
}

abstract class BusinessDayCalculatorBase {
    private static publicHolidaysCache: { [year: number]: Date[] } = {};

    protected abstract get publicHolidays(): IDayDefinition[];

    public getLastBusinessDay(currentDate: Date): Date {
        return this.getBusinessDay(currentDate, d => d.setDate(d.getDate() - 1));
    }

    public getNextBusinessDay(currentDate: Date): Date {
        return this.getBusinessDay(currentDate, d => d.setDate(d.getDate() + 1));
    }

    public addBusinessDays(startDate: Date, days: number): Date {
        const date = new Date(startDate.getTime());
        let remainingDays = days;

        while (remainingDays > 0) {
            date.setDate(date.getDate() + 1);
            if (!this.isNonBusinessDay(date)) remainingDays--;
        }

        return date;
    }

    public getPublicHolidays(year: number): Date[] {
        if (year in BusinessDayCalculatorBase.publicHolidaysCache) {
            return BusinessDayCalculatorBase.publicHolidaysCache[year];
        }

        const publicHolidays: Date[] = [];
        for (const publicHolidayDef of this.publicHolidays) {
            const holiday = publicHolidayDef.calculateDate(year);
            if (holiday != null)
                publicHolidays.push(holiday);
        }

        return BusinessDayCalculatorBase.publicHolidaysCache[year] = publicHolidays;
    }

    public isPublicHoliday(date: Date): boolean {
        const publicHolidays = this.getPublicHolidays(date.getFullYear());
        return publicHolidays.some(ph => {
            return ph.getFullYear() === date.getFullYear() &&
                ph.getMonth() === date.getMonth() &&
                ph.getDate() === date.getDate();
        });
    }

    private getBusinessDay(currentDate: Date, transformFunc: (date: Date) => void): Date {
        const date = new Date(currentDate.getTime());

        while (this.isNonBusinessDay(date)) {
            transformFunc(date);
        }

        return date;
    }

    private isNonBusinessDay(date: Date): boolean {
        return CalendarUtils.isWeekend(date) || this.isPublicHoliday(date);
    }
}

class BusinessDayCalculatorPLBase extends BusinessDayCalculatorBase{
    protected publicHolidays: IDayDefinition[] = [
        {
            name: 'New Year\'s Day',
            type: DayType.PublicHoliday,
            calculateDate: year => new Date(year, 0, 1)
        },
        {
            name: 'Epiphany',
            type: DayType.PublicHoliday,
            calculateDate: year => new Date(year, 0, 6)
        },
        {
            name: 'Easter Sunday',
            type: DayType.PublicHoliday,
            calculateDate: year => CalendarUtils.calculateEasterSunday(year)
        },
        {
            name: 'Easter Monday',
            type: DayType.PublicHoliday,
            calculateDate: year => addDays(CalendarUtils.calculateEasterSunday(year), 1)
        },
        {
            name: 'Labour Day',
            type: DayType.PublicHoliday,
            calculateDate: (year: number): Date => new Date(year, 4, 1)
        },
        {
            name: 'Constitution Day',
            type: DayType.PublicHoliday,
            calculateDate: (year: number): Date => new Date(year, 4, 3)
        },
        {
            name: 'Pentecost Sunday',
            type: DayType.PublicHoliday,
            calculateDate: year => addDays(CalendarUtils.calculateEasterSunday(year), 7 * 7)
        },
        {
            name: 'Corpus Christi',
            type: DayType.PublicHoliday,
            calculateDate: year => addDays(CalendarUtils.calculateEasterSunday(year), 60)
        },
        {
            name: 'Assumption of the Blessed Virgin Mary',
            type: DayType.PublicHoliday,
            calculateDate: (year: number): Date => new Date(year, 7, 15)
        },
        {
            name: 'All Saints\' Day',
            type: DayType.PublicHoliday,
            calculateDate: (year: number): Date => new Date(year, 10, 1)
        },
        {
            name: 'Independence Day',
            type: DayType.PublicHoliday,
            calculateDate: (year: number): Date => new Date(year, 10, 11)
        },
        {
            name: 'Independence Day 2018',
            type: DayType.PublicHoliday,
            calculateDate: (year: number): Date | null => year === 2018 ? new Date(2018, 10, 12) : null
        },
        {
            name: 'Christmas Eve',
            type: DayType.PublicHoliday,
            calculateDate: (year: number): Date => new Date(year, 11, 24)
        },
        {
            name: 'Christmas Day',
            type: DayType.PublicHoliday,
            calculateDate: (year: number): Date => new Date(year, 11, 25)
        },
        {
            name: 'Second Day of Christmastide',
            type: DayType.PublicHoliday,
            calculateDate: (year: number): Date => new Date(year, 11, 26)
        }
    ];
};
export const BusinessDayCalculatorPL = new BusinessDayCalculatorPLBase();